import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown, Calendar, Copy, Image as ImageIcon, LayoutList, Trash2 } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { z } from 'zod'

import {
	MarketingContentFormat,
	type MarketingContent,
	type MarketingContentFormatType
} from '@/api/admin-marketing-content-types'
import { useCreateMarketingContent, useDeleteMarketingContent, useDuplicateMarketingContent } from '@/api/api-mutations'
import { useListMarketingChannels, useListMarketingContent } from '@/api/react-query'
import { AssetImage } from '@/components/base/asset-image'
import { Badge } from '@/components/base/badge'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { DropdownMenu } from '@/components/base/dropdown-menu'
import { Heading } from '@/components/base/heading'
import { IconButton } from '@/components/base/icon-button'
import { SearchField } from '@/components/base/search-field'
import { StatusCell } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { Tooltip } from '@/components/base/tooltip'
import { SingleColumnPage } from '@/components/page-layout'
import { FilterButton } from '@/components/table/filter-button'
import { useI18n } from '@/i18n/i18n-react'
import { Plus } from '@/icons/plus'
import { clx } from '@/utils/clx'
import { restoreListFilters, usePersistListFilters } from '@/utils/list-filter-persistence'

import { ContentFilterDrawer } from './-content-filter-drawer'

const CONTENT_FILTER_STORAGE_KEY = 'marketing-content'
const CONTENT_FILTER_KEYS = ['q', 'formats', 'channels'] as const

export const Route = createFileRoute('/_authenticated/_general/marketing/content/')({
	component: MarketingContentListPage,
	validateSearch: z.object({
		q: z.string().optional(),
		formats: z.array(z.string()).optional(),
		channels: z.array(z.string()).optional()
	}),
	beforeLoad: ({ search, location, cause }) =>
		restoreListFilters({
			storageKey: CONTENT_FILTER_STORAGE_KEY,
			to: '/marketing/content',
			pathname: location.pathname,
			cause,
			keys: CONTENT_FILTER_KEYS,
			search
		}),
	staticData: {
		crumb: ({ context: { L } }) => L.Marketing.Content.Title()
	}
})

type WindowStatus = 'ACTIVE' | 'SCHEDULED' | 'DRAFT' | 'ENDED'

const WINDOW_STATUS_ORDER: WindowStatus[] = ['ACTIVE', 'SCHEDULED', 'DRAFT', 'ENDED']

const deriveWindowStatus = (content: MarketingContent, now: number): WindowStatus => {
	if (content.status === 'DRAFT') return 'DRAFT'
	if (content.endsAt && content.endsAt <= now) return 'ENDED'
	if (content.startsAt && content.startsAt > now) return 'SCHEDULED'
	return 'ACTIVE'
}

function MarketingContentListPage() {
	const { L } = useI18n()
	const navigate = useNavigate()
	const search = Route.useSearch()
	const { q, formats = [], channels = [] } = search
	usePersistListFilters(CONTENT_FILTER_STORAGE_KEY, CONTENT_FILTER_KEYS, search)
	const [filtersOpen, setFiltersOpen] = useState(false)
	const hasFilters = Boolean(q) || formats.length > 0 || channels.length > 0

	// Volume is small (a tenant has at most a few dozen content items); render the full set
	// client-side so we can group by derived window status without paginating.
	const contentQuery = useListMarketingContent({
		limit: 100,
		offset: 0,
		q,
		formats: formats.length > 0 ? (formats as MarketingContentFormatType[]) : undefined,
		channels: channels.length > 0 ? channels : undefined
	})

	const createContent = useCreateMarketingContent()
	const deleteContent = useDeleteMarketingContent()
	const duplicateContent = useDuplicateMarketingContent()

	const now = Date.now()
	const allRows = contentQuery.data?.content ?? []

	const grouped = useMemo(() => {
		const groups: Record<WindowStatus, MarketingContent[]> = { ACTIVE: [], SCHEDULED: [], DRAFT: [], ENDED: [] }
		for (const c of allRows) groups[deriveWindowStatus(c, now)].push(c)
		groups.ACTIVE.sort(
			(a, b) => (a.endsAt ?? Infinity) - (b.endsAt ?? Infinity) || (a.startsAt ?? 0) - (b.startsAt ?? 0)
		)
		groups.SCHEDULED.sort((a, b) => (a.startsAt ?? Infinity) - (b.startsAt ?? Infinity))
		groups.DRAFT.sort((a, b) => b.updatedAt - a.updatedAt)
		groups.ENDED.sort((a, b) => (b.endsAt ?? 0) - (a.endsAt ?? 0))
		return groups
	}, [allRows, now])

	const handleCreate = async (format: MarketingContentFormatType) => {
		const name =
			format === MarketingContentFormat.CheckoutBackground
				? L.Marketing.ContentLayout.NewBackground()
				: L.Marketing.Content.NewContent()
		try {
			const result = await createContent({ body: { name, format } })
			if (result.isSuccess) {
				navigate({ to: '/marketing/content/$contentUuid', params: { contentUuid: result.response.uuid } })
			}
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleDuplicate = async (content: MarketingContent) => {
		try {
			const result = await duplicateContent({ path: { contentUuid: content.uuid } })
			if (result.isSuccess) {
				toast.success(L.Marketing.Content.DuplicateSuccess())
				navigate({ to: '/marketing/content/$contentUuid', params: { contentUuid: result.response.uuid } })
			}
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const handleDelete = async (content: MarketingContent) => {
		if (!window.confirm(L.Marketing.Content.DeleteConfirm())) return
		try {
			const result = await deleteContent({ path: { contentUuid: content.uuid } })
			if (!result.isSuccess) {
				toast.error(typeof result.response === 'string' ? result.response : L.General.Unknown())
			}
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		}
	}

	const showingEmptyOverall = contentQuery.isSuccess && allRows.length === 0

	// A tenant with no content at all gets the "create your first" empty state instead of a toggle
	// that has nothing to switch between.
	const showTypeToggle = !contentQuery.isError && !(showingEmptyOverall && !hasFilters)

	let listBody: ReactNode
	if (contentQuery.isError) {
		listBody = (
			<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
				<Heading level='h3'>{L.ApiErrorAlert.FailedToLoadData()}</Heading>
				<Text className='max-w-md text-ui-fg-subtle'>{L.ApiErrorAlert.AnErrorOccurredWhileFetchingData()}</Text>
				<Button type='button' variant='secondary' onClick={() => contentQuery.refetch()}>
					{L.General.TryAgain()}
				</Button>
			</div>
		)
	} else if (contentQuery.isLoading) {
		listBody = null
	} else if (showingEmptyOverall && hasFilters) {
		listBody = (
			<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
				<Heading level='h3'>{L.Marketing.Content.NoMatches()}</Heading>
				<Text className='max-w-sm text-ui-fg-subtle'>{L.Marketing.Content.NoMatchesDescription()}</Text>
				<Button type='button' variant='secondary' onClick={() => navigate({ to: '.', search: {} })}>
					{L.Marketing.Content.ClearFilters()}
				</Button>
			</div>
		)
	} else if (showingEmptyOverall) {
		listBody = (
			<div className='flex flex-col items-center justify-center gap-3 rounded-md border border-dashed border-ui-border-base p-12 text-center'>
				<Calendar className='size-10 text-ui-fg-muted' />
				<Heading level='h3'>{L.Marketing.Content.NoContent()}</Heading>
				<Text className='max-w-sm text-ui-fg-subtle'>{L.Marketing.Content.NoContentDescription()}</Text>
				<NewContentMenu onCreate={handleCreate}>
					<Button>{L.Marketing.Content.NewContent()}</Button>
				</NewContentMenu>
			</div>
		)
	} else {
		const renderRow = (content: MarketingContent, windowStatus: WindowStatus) => (
			<ContentRow
				key={content.uuid}
				content={content}
				windowStatus={windowStatus}
				onDuplicate={() => handleDuplicate(content)}
				onDelete={() => handleDelete(content)}
			/>
		)
		const sections: { status: WindowStatus; title: string; statusColor: SectionProps['statusColor'] }[] = [
			{ status: 'ACTIVE', title: L.Marketing.Campaign.ActiveNow(), statusColor: 'green' },
			{ status: 'SCHEDULED', title: L.Marketing.Campaign.Scheduled(), statusColor: 'blue' },
			{ status: 'DRAFT', title: L.Marketing.Campaign.Draft(), statusColor: 'grey' },
			{ status: 'ENDED', title: L.Marketing.Campaign.Ended(), statusColor: 'red' }
		]
		const firstSection = WINDOW_STATUS_ORDER.find(status => grouped[status].length > 0)
		// One grid for every section, so the meta column lines up across all rows. Columns: type icon,
		// name, channels/blocks, duplicate, delete.
		listBody = (
			<div className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto] gap-x-3 gap-y-1.5'>
				{sections.map(({ status, title, statusColor }) => (
					<Section
						key={status}
						title={title}
						statusColor={statusColor}
						items={grouped[status]}
						isFirst={status === firstSection}
						collapsedByDefault={status === 'ENDED'}
					>
						{c => renderRow(c, status)}
					</Section>
				))}
			</div>
		)
	}

	return (
		<SingleColumnPage>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader
					heading={L.Marketing.Content.Title()}
					rightSide={
						<>
							<SearchField />
							<FilterButton onClick={() => setFiltersOpen(true)} activeCount={channels.length} />
							<NewContentMenu onCreate={handleCreate}>
								<IconButton tooltipText={L.Marketing.Content.NewContent()}>
									<Plus />
								</IconButton>
							</NewContentMenu>
						</>
					}
				/>
				{showTypeToggle && (
					<div className='px-4 pt-4 sm:px-6'>
						<TypeToggle
							value={formats.length === 1 ? (formats[0] as MarketingContentFormatType) : null}
							onChange={format =>
								navigate({ to: '.', search: { ...search, formats: format ? [format] : undefined } })
							}
						/>
					</div>
				)}
				<div className='flex-1 overflow-y-auto p-4 sm:p-6'>{listBody}</div>
				<ContentFilterDrawer
					open={filtersOpen}
					onOpenChange={setFiltersOpen}
					defaultValues={{ channels }}
					onApply={filters =>
						navigate({
							to: '.',
							search: { ...search, channels: filters.channels.length > 0 ? filters.channels : undefined }
						})
					}
				/>
			</Container>
		</SingleColumnPage>
	)
}

// Content is created for one format up front, so the manager picks what they're making before the
// editor opens: a free-form feed item, or a single-image checkout background.
function NewContentMenu({
	onCreate,
	children
}: {
	onCreate: (format: MarketingContentFormatType) => void
	children: ReactNode
}) {
	const { L } = useI18n()
	const options = [
		{
			format: MarketingContentFormat.Feed,
			label: L.Marketing.Content.NewFeedContent(),
			hint: L.Marketing.Content.NewFeedContentHint()
		},
		{
			format: MarketingContentFormat.CheckoutBackground,
			label: L.Marketing.Content.NewCheckoutBackground(),
			hint: L.Marketing.Content.NewCheckoutBackgroundHint()
		}
	]
	return (
		<DropdownMenu>
			<DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
			<DropdownMenu.Content align='end' className='z-50 w-72'>
				{options.map(o => (
					<DropdownMenu.Item
						key={o.format}
						className='flex-col items-start gap-0.5'
						onClick={() => onCreate(o.format)}
					>
						<Text className='font-medium'>{o.label}</Text>
						<Text className='text-xs text-ui-fg-subtle'>{o.hint}</Text>
					</DropdownMenu.Item>
				))}
			</DropdownMenu.Content>
		</DropdownMenu>
	)
}

type SectionProps = {
	title: string
	statusColor: 'green' | 'blue' | 'grey' | 'red'
	items: MarketingContent[]
	children: (item: MarketingContent) => ReactNode
	collapsedByDefault?: boolean
	isFirst?: boolean
}

// Rendered as `display: contents` so its rows join the parent list grid.
function Section({ title, statusColor, items, children, collapsedByDefault = false, isFirst = false }: SectionProps) {
	const [collapsed, setCollapsed] = useState(collapsedByDefault)
	if (items.length === 0) return null
	return (
		<section className='contents'>
			<div className={clx('col-span-full mb-0.5 flex items-center', !isFirst && 'mt-6')}>
				<button
					type='button'
					onClick={() => setCollapsed(!collapsed)}
					className='flex items-center gap-2 text-left'
				>
					<StatusCell color={statusColor}>{title}</StatusCell>
					<Text className='text-ui-fg-subtle'>{items.length}</Text>
					<ChevronDown className={clx('size-4 transition-transform', collapsed && '-rotate-90')} />
				</button>
			</div>
			{!collapsed && items.map(item => children(item))}
		</section>
	)
}

function ContentRow({
	content,
	windowStatus,
	onDuplicate,
	onDelete
}: {
	content: MarketingContent
	windowStatus: WindowStatus
	onDuplicate: () => void
	onDelete: () => void
}) {
	const { L } = useI18n()
	return (
		<div className='group col-span-full grid grid-cols-subgrid items-center rounded-md border border-ui-border-base bg-ui-bg-base px-3 py-2.5 hover:bg-ui-bg-base-hover'>
			<Link
				to='/marketing/content/$contentUuid'
				params={{ contentUuid: content.uuid }}
				className='col-span-3 grid grid-cols-subgrid items-center text-left'
			>
				<ContentThumbnail content={content} />
				<div className='flex min-w-0 flex-1 flex-col'>
					<Text className='truncate font-medium'>{content.name}</Text>
					<ScheduleSummary content={content} windowStatus={windowStatus} />
				</div>
				<ContentMeta content={content} />
			</Link>
			<IconButton
				variant='transparent'
				size='small'
				tooltipText={L.Marketing.Campaign.Action.Duplicate()}
				onClick={e => {
					e.stopPropagation()
					onDuplicate()
				}}
			>
				<Copy className='size-4 text-ui-fg-muted opacity-0 transition-opacity group-hover:opacity-100' />
			</IconButton>
			<IconButton
				variant='transparent'
				size='small'
				tooltipText={L.Marketing.Campaign.Action.Delete()}
				onClick={e => {
					e.stopPropagation()
					onDelete()
				}}
			>
				<Trash2 className='size-4 text-ui-fg-muted opacity-0 transition-opacity group-hover:opacity-100' />
			</IconButton>
		</div>
	)
}

// The thumbnail's shape tells the type apart in the "All" view: checkout screens are landscape like
// the exit screen, feed content is square. Content with no image yet shows the type icon instead.
function ContentThumbnail({ content }: { content: MarketingContent }) {
	const { L } = useI18n()
	const isCheckout = content.format === MarketingContentFormat.CheckoutBackground
	const Icon = isCheckout ? ImageIcon : LayoutList
	const frame = clx('shrink-0 overflow-hidden rounded', isCheckout ? 'h-[30px] w-12' : 'size-8')
	return (
		<Tooltip content={L.Marketing.Content.Format(content.format)}>
			<span className='flex w-12 justify-center'>
				{content.previewAsset ? (
					<AssetImage asset={content.previewAsset} preferredVariant='THUMBNAIL' className={frame} />
				) : (
					<span className={clx(frame, 'flex items-center justify-center bg-ui-bg-subtle text-ui-fg-subtle')}>
						<Icon className='size-4' />
					</span>
				)}
			</span>
		</Tooltip>
	)
}

const MAX_LISTED_CHANNELS = 2

// Red is left out: on a pill it reads as an error.
const CHANNEL_COLORS = ['blue', 'green', 'orange', 'purple', 'grey'] as const
type ChannelColor = (typeof CHANNEL_COLORS)[number]

// A channel keeps the same pill colour on every row, assigned in the channel list's order so the
// first few channels never share a colour.
const useChannelColors = () => {
	const channelsQuery = useListMarketingChannels()
	return useMemo(() => {
		const colors = new Map<string, ChannelColor>()
		;(channelsQuery.data ?? []).forEach((c, i) => colors.set(c.uuid, CHANNEL_COLORS[i % CHANNEL_COLORS.length]))
		return colors
	}, [channelsQuery.data])
}

// Where the content runs, then its size. It fills the list grid's shared meta column, so every row's
// first channel starts at the same place. Format already implies the surface (feed vs checkout
// screen), so a placement only names the channel. A checkout background is always a single image,
// so its block count is left out.
function ContentMeta({ content }: { content: MarketingContent }) {
	const { L } = useI18n()
	const channelColors = useChannelColors()
	const channels = [...new Map(content.placements.map(p => [p.marketingChannelUuid, p.marketingChannelName]))]
	const showBlockCount = content.format !== MarketingContentFormat.CheckoutBackground
	const blockCount = showBlockCount ? L.Marketing.Content.BlockCount({ count: content.blockCount }) : null
	const hidden = channels.length - MAX_LISTED_CHANNELS

	let placement: ReactNode
	if (channels.length === 0) {
		placement = <Text className='text-xs text-ui-fg-muted'>{L.Marketing.Content.NotInLayout()}</Text>
	} else {
		const pills = (
			<span className='flex min-w-0 items-center gap-1'>
				{channels.slice(0, MAX_LISTED_CHANNELS).map(([uuid, name]) => (
					<Badge
						key={uuid}
						size='2xsmall'
						color={channelColors.get(uuid) ?? 'grey'}
						className='max-w-32 truncate whitespace-nowrap'
					>
						{name}
					</Badge>
				))}
				{hidden > 0 && <Text className='text-xs text-ui-fg-subtle'>+{hidden}</Text>}
			</span>
		)
		placement =
			hidden > 0 ? <Tooltip content={channels.map(([, name]) => name).join(', ')}>{pills}</Tooltip> : pills
	}

	return (
		<div className='hidden min-w-0 max-w-80 items-center gap-1.5 sm:flex'>
			{placement}
			{blockCount && <Text className='whitespace-nowrap text-xs text-ui-fg-subtle'>· {blockCount}</Text>}
		</div>
	)
}

const TYPE_OPTIONS = ['ALL', MarketingContentFormat.Feed, MarketingContentFormat.CheckoutBackground] as const

function TypeToggle({
	value,
	onChange
}: {
	value: MarketingContentFormatType | null
	onChange: (format: MarketingContentFormatType | null) => void
}) {
	const { L } = useI18n()
	return (
		<div className='inline-flex shrink-0 rounded-md border border-ui-border-base bg-ui-bg-base p-0.5'>
			{TYPE_OPTIONS.map(option => {
				const selected = option === 'ALL' ? value === null : value === option
				return (
					<Button
						key={option}
						variant='transparent'
						size='small'
						aria-pressed={selected}
						onClick={() => onChange(option === 'ALL' ? null : option)}
						className={clx(
							'rounded text-xs font-medium',
							selected
								? 'bg-ui-bg-component text-ui-fg-base shadow-borders-base'
								: 'text-ui-fg-subtle hover:text-ui-fg-base'
						)}
					>
						{L.Marketing.Content.TypeOption(option)}
					</Button>
				)
			})}
		</div>
	)
}

function ScheduleSummary({ content, windowStatus }: { content: MarketingContent; windowStatus: WindowStatus }) {
	const { L, locale } = useI18n()
	const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' })
	const fmt = (ms: number) => formatter.format(new Date(ms))

	if (windowStatus === 'DRAFT')
		return <Text className='text-xs text-ui-fg-muted'>{L.Marketing.Campaign.Draft()}</Text>
	if (windowStatus === 'SCHEDULED' && content.startsAt) {
		return (
			<Text className='text-xs text-ui-fg-subtle'>
				{L.Marketing.Campaign.Field.StartsAt()}: {fmt(content.startsAt)}
			</Text>
		)
	}
	if (windowStatus === 'ACTIVE') {
		return (
			<Text className='text-xs text-ui-fg-subtle'>
				{content.startsAt ? fmt(content.startsAt) : ''}
				{content.endsAt ? ` → ${fmt(content.endsAt)}` : ` → ${L.Marketing.Campaign.Field.EndsAtOpenEnded()}`}
			</Text>
		)
	}
	if (windowStatus === 'ENDED' && content.endsAt) {
		return (
			<Text className='text-xs text-ui-fg-muted'>
				{L.Marketing.Campaign.Field.EndsAt()}: {fmt(content.endsAt)}
			</Text>
		)
	}
	return null
}
